import { useEffect, useCallback, memo } from 'react'
import ReactHTMLParser, { convertNodeToElement } from 'react-html-parser'
import Markdown from 'markdown-it'
import PropTypes from 'prop-types'

import styles from './style.module.css'

import appConfig from '@config/app'
import customContainerMdPlugin from '@utils/customContainerMdPlugin'
import { getPriceCurrency } from '@utils/helpers'

function MarkdownContainer({
  title,
  content,
  image,
  imageAlt,
  textColor,
  searchQuery,
  isContentCentered,
  isSmallParagraph,
  parseOnlyFirstParagraph,
  trackEvent,
}) {
  useEffect(() => {
    if (window && window.instgrm && window.instgrm.Embeds) {
      window.instgrm.Embeds.process()
    }
  }, [])

  const getTextColorStyle = useCallback(() => {
    switch (textColor) {
      case 'white':
        return styles.white
      case 'black':
        return styles.black
      case 'nightRider':
      default:
        return styles.nightRider
    }
  }, [textColor])

  const transformNode = useCallback(
    (node, index) => {
      // Link tags
      if (node.type === 'tag' && node.name === 'a') {
        node.attribs = {
          ...(node.attribs ? node.attribs : {}),
          class: styles.link,
          target: '_blank',
        }
      }

      // Title Image tags
      if (
        node.type === 'tag' &&
        node.name === 'img' &&
        node.attribs.alt === title
      ) {
        node.attribs = { ...node.attribs, class: styles.titleImage }
      }
      if (
        node.type === 'tag' &&
        node.name === 'img' &&
        node.attribs.class !== styles.tipsLogo
      ) {
        node.attribs = { ...node.attribs, class: styles.image }
      }
      if (
        node.type === 'tag' &&
        node.name === 'img' &&
        node.attribs.class.includes('productImage')
      ) {
        node.attribs = {
          ...node.attribs,
          class: node.attribs.class.replace(
            'productImage',
            styles.productImage
          ),
        }
      }

      // Paragraph tags
      if (node.type === 'tag' && node.name === 'p' && !node.parent) {
        node.attribs = {
          class: `${
            parseOnlyFirstParagraph
              ? styles.firstParagraph
              : isSmallParagraph
              ? styles.smallParagraph
              : styles.normalParagraph
          } ${getTextColorStyle()}`,
        }
      }
      if (
        node.type === 'tag' &&
        node.name === 'p' &&
        node.parent &&
        node.parent.name === 'blockquote'
      ) {
        node.attribs = { class: styles.quoteParagraph }
      }
      if (
        node.type === 'tag' &&
        node.name === 'p' &&
        node.parent &&
        node.parent.attribs.class === 'references'
      ) {
        node.attribs = { class: styles.referencesParagraph }
      }
      if (
        node.type === 'tag' &&
        node.name === 'p' &&
        node.attribs.class === 'productName'
      ) {
        node.attribs = { class: styles.productName }
      }
      if (
        node.type === 'tag' &&
        node.name === 'p' &&
        node.attribs.class === 'productDescription'
      ) {
        node.attribs = { class: styles.productDescription }
      }
      if (
        node.type === 'tag' &&
        node.name === 'p' &&
        node.attribs.class === 'productRating'
      ) {
        node.attribs = { class: styles.productRating }
      }
      if (
        node.type === 'tag' &&
        node.name === 'p' &&
        node.attribs.class === 'productPrice'
      ) {
        node.attribs = { class: styles.productPrice }

        const priceText = node.children.find(
          childNode => childNode.type === 'text'
        )
        if (priceText) priceText.data = `Rp ${getPriceCurrency(priceText.data)}`

        const strikeTag = node.children.find(
          childNode => childNode.type === 'tag' && childNode.name === 'strike'
        )
        if (strikeTag) {
          const strikePriceText = strikeTag.children.find(
            childNode => childNode.type === 'text'
          )
          if (strikePriceText) {
            strikePriceText.data = `Rp ${getPriceCurrency(
              strikePriceText.data
            )}`
          }
        }
      }

      // List tags
      if (node.type === 'tag' && /ul|ol/.test(node.name)) {
        node.attribs = { class: styles.list }
      }
      if (
        node.type === 'tag' &&
        /ul|ol/.test(node.name) &&
        node.parent &&
        node.parent.attribs.class === 'references'
      ) {
        node.attribs = { class: styles.referencesList }
      }

      // Heading tags
      if (
        node.type === 'tag' &&
        /h[1-6]{1}/.test(node.name) &&
        !node.attribs.class
      ) {
        node.attribs = { class: styles[`heading${node.name[1]}`] }
      }

      // Blockquote tags
      if (
        node.type === 'tag' &&
        node.name === 'blockquote' &&
        node.attribs &&
        node.attribs.class !== 'instagram-media'
      ) {
        node.attribs = { class: styles.blockquote }
      }

      // Hr tags
      if (node.type === 'tag' && node.name === 'hr') {
        node.attribs = { class: styles.hr }
      }

      // Div tags
      if (
        node.type === 'tag' &&
        node.name === 'div' &&
        node.attribs.class === 'basetips'
      ) {
        node.attribs = { class: styles.baseTips }
      }
      if (
        node.type === 'tag' &&
        node.name === 'div' &&
        node.attribs.class === 'image-with-caption'
      ) {
        node.attribs = { class: styles.imageWithCaption }
      }
      if (
        node.type === 'tag' &&
        node.name === 'div' &&
        node.attribs.class === 'instagram-post'
      ) {
        node.attribs = { class: styles.instagramPost }
      }
      if (
        node.type === 'tag' &&
        node.name === 'div' &&
        node.attribs.class === 'products'
      ) {
        node.attribs = { class: styles.products }
      }
      if (
        node.type === 'tag' &&
        node.name === 'div' &&
        node.attribs.class === 'product'
      ) {
        node.attribs = { class: styles.productContainer }
      }

      // Iframe tags
      if (node.type === 'tag' && node.name === 'iframe') {
        node.attribs = {
          ...(node.attribs ? node.attribs : {}),
          class: styles.iframe,
        }
      }

      // Button tags
      if (
        node.type === 'tag' &&
        node.name === 'button' &&
        node.attribs.class === 'productButton'
      ) {
        return (
          <div
            className={styles.productButton}
            onClick={event => {
              event.preventDefault()

              if (trackEvent) {
                trackEvent('blog_to_product_catalog', {
                  event_category: `product_name:${node.attribs['data-product-name']}`,
                  event_label: `blog_title:${title}`,
                })
              }

              let host = ''

              switch (appConfig.environment) {
                case 'production':
                  host = 'https://www.base.co.id/products'
                  break
                case 'development':
                  host = 'https://dev.www.base.co.id/products'
                  break
                default:
                  host = 'http://localhost:3000/products'
              }

              window.location.href = `${host}/${node.attribs['data-product-url']}`
            }}
          >
            SHOP NOW
          </div>
        )
      }

      // Tables tag
      if (
        node.type === 'tag' &&
        /^t.*/.test(node.name) &&
        !/^t(e|i|ra)+.*/.test(node.name)
      ) {
        switch (node.name) {
          case 'table':
            node.attribs = { class: styles.table }
            break
          case 'thead':
            node.attribs = { class: styles.thead }
            break
          case 'tbody':
            node.attribs = { class: styles.tbody }
            break
          case 'tr':
            if (node.parent && node.parent.name == 'thead') {
              node.attribs = { class: styles.trhead }
            }
            if (node.parent && node.parent.name === 'tbody') {
              node.attribs = { class: styles.trbody }
            }
            break
          case 'th':
            node.attribs = { class: styles.th }
            break
          case 'td':
            node.attribs = { class: styles.td }
            break
        }
      }

      // Text coloring for majority
      if (
        !/blockquote|div|img/.test(node.name) &&
        !(node.type !== 'text') &&
        !(
          node.name === 'p' &&
          node.attribs &&
          /quoteparagraph/i.test(node.attribs.class)
        ) &&
        !(
          node.name === 'span' &&
          node.attribs &&
          /(left|right)quote/i.test(node.attribs.class)
        ) &&
        !(node.type === 'tag' && node.name === 'a') &&
        !(
          node.type === 'tag' &&
          /ul|ol/.test(node.name) &&
          node.parent &&
          node.parent.attribs.class === 'references'
        ) &&
        !(
          node.type === 'tag' &&
          node.name === 'p' &&
          node.parent &&
          node.parent.attribs.class === 'references'
        ) &&
        !(node.type === 'tag' && /li|b|em|iframe/.test(node.name))
      ) {
        node.attribs = {
          class: `${
            node.attribs ? node.attribs.class : undefined
          } ${getTextColorStyle()}`,
        }
      }

      return convertNodeToElement(node, index, transformNode)
    },
    [
      title,
      isSmallParagraph,
      parseOnlyFirstParagraph,
      getTextColorStyle,
      trackEvent,
    ]
  )

  const parseMarkdown = useCallback(() => {
    const MD = new Markdown({ html: true, typographer: true, linkify: false })
      .use(customContainerMdPlugin, 'basetips')
      .use(customContainerMdPlugin, 'image-with-caption')
      .use(customContainerMdPlugin, 'instagram-post')
      .use(customContainerMdPlugin, 'references')
      .use(customContainerMdPlugin, 'products')

    const markdownToRender = parseOnlyFirstParagraph
      ? content
      : (title ? `# ${title}\n\n` : '') +
        (image ? `![${imageAlt || title || 'Main Image'}](${image})\n\n` : '') +
        content

    return MD.render(markdownToRender)
  }, [content, image, imageAlt, parseOnlyFirstParagraph, title])

  const renderHTMLToJSX = useCallback(
    data => {
      const contents = data.split('\n')

      if (contents.length < 2) {
        if (searchQuery) {
          contents[0] = contents[0].replace(
            new RegExp(`(${searchQuery})`, 'gi'),
            '<b>$1</b>'
          )
        }
        return ReactHTMLParser(contents[0], { transform: transformNode })
      }

      for (let start = 0; start < contents.length; start++) {
        // --- CUSTOM QUOTE ---
        if (
          /<blockquote.*>/.test(contents[start]) &&
          !/instagram-media/.test(contents[start])
        ) {
          contents.splice(
            start + 1,
            0,
            `<span class="${styles.leftQuote}">&ldquo;</span>`
          )
          start++
        }

        if (
          /<\/blockquote.*>/.test(contents[start]) &&
          !/instagram-media/.test(contents[start])
        ) {
          contents.splice(
            start,
            0,
            `<span class="${styles.rightQuote}">&rdquo;</span>`
          )
          start++
        }
        // --- END OF CUSTOM QUOTE ---

        // --- CUSTOM BASETIPS ---
        if (/basetips/.test(contents[start])) {
          contents.splice(
            start + 1,
            0,
            // add logo
            `<div class="${styles.logoContainer}">` +
              `<img alt="basetips-hand" src="https://static.base.co.id/ok-hand.svg" alt="tips" class="${styles.tipsLogo}" />` +
              `</div>`,
            // add opening container
            `<div class="${styles.tipsContainer}"><p class="${styles.tipsHeader}">Base Tips</p>`
          )
          start++
        }

        if (/^<\/div>$/.test(contents[start])) {
          contents.splice(start, 0, '</div>')
          start++
        }
        // --- END OF CUSTOM BASETIPS ---
      }

      return ReactHTMLParser(contents.join(''), { transform: transformNode })
    },
    [searchQuery, transformNode]
  )

  const parsedContent = parseMarkdown()
  const matchedFirstParagraph = parsedContent.match(/<p>.*<\/p>/g)
  let firstParagraph = matchedFirstParagraph ? matchedFirstParagraph[0] : ''

  if (searchQuery) {
    const regexSearch = new RegExp(`.*${searchQuery}.*`, 'gi')
    const matchedContent = parsedContent.match(regexSearch)

    if (matchedContent) {
      firstParagraph = `<p>${matchedContent[0].replace(/<[^>]*>?/gm, '')}</p>`
    }
  }

  return (
    <div
      className={`
            ${styles.container}
            ${isContentCentered && styles.centered}
          `}
    >
      {parseOnlyFirstParagraph
        ? renderHTMLToJSX(firstParagraph)
        : renderHTMLToJSX(parsedContent)}
    </div>
  )
}

MarkdownContainer.propTypes = {
  title: PropTypes.string,
  content: PropTypes.string,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  textColor: PropTypes.string,
  searchQuery: PropTypes.string,
  isContentCentered: PropTypes.bool,
  isSmallParagraph: PropTypes.bool,
  parseOnlyFirstParagraph: PropTypes.bool,
  trackEvent: PropTypes.func,
}

export default memo(MarkdownContainer)
